/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useEffect, type RefObject } from 'react';

/**
 * ⌘K / Ctrl K, and the one place that knows which.
 *
 * This lived inside `BoardSearch`, which meant the board had a shortcut and the
 * other three searches — List, Archived, My Day — had nothing, and no hint
 * either. Somebody who learns ⌘K on the board and presses it on My Day gets
 * their browser's own bookmark dialog instead, which is worse than never having
 * offered it.
 *
 * **Only one search is ever mounted.** The topbar's board search renders only on
 * the board (`onBoard && hasCircle` in `AppLayout`), and the other three belong
 * to views that are mutually exclusive routes. So a global `keydown` per search
 * cannot collide with another one — but that is a property of the layout rather
 * than of this hook, which is why it is written down here.
 */

/**
 * `⌘K` on a Mac, `Ctrl K` everywhere else — printed, not guessed at.
 *
 * Read from the user agent rather than from `navigator.platform`, which is
 * deprecated and already lies on some browsers. Getting it wrong is worse than
 * omitting it: a hint naming a key that does nothing on your machine is a bug
 * you cannot report.
 *
 * Computed once at module load. It cannot change without a reload, and reading
 * it per render would be one more thing to keep in step for no gain.
 */
export const SEARCH_HINT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘K' : 'Ctrl K';

/** Whether this keystroke is the search shortcut, on either platform. */
export function isSearchShortcut(e: KeyboardEvent): boolean {
  // `metaKey || ctrlKey` rather than one per platform: a Mac keyboard plugged
  // into Windows, and vice versa, are both real in this office. `altKey` is
  // excluded because ⌥⌘K and Ctrl+Alt+K are other applications' shortcuts and
  // stealing them is how a web app becomes the reason somebody's window manager
  // "broke".
  return e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey) && !e.altKey;
}

/**
 * Put the cursor in this search from anywhere on the page.
 *
 * `onBeforeFocus` is for the board, where the box is not on screen below
 * `laptop` and has to be opened first; every other search is always mounted and
 * passes nothing.
 *
 * The text is **selected**, not just focused, so the next keystroke replaces a
 * search somebody has forgotten is still applied rather than appending to it.
 */
export function useSearchShortcut(
  input: RefObject<HTMLInputElement | null>,
  onBeforeFocus?: () => void,
): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isSearchShortcut(e)) return;
      // Not while a dialog is open. The search is behind it, Radix's focus trap
      // takes the focus straight back, and what you see is a shortcut that
      // flickers and does nothing. Escape closes the dialog first; that is the
      // order, and it is the same order the browser's own dialogs use.
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      onBeforeFocus?.();
      // After the caller's state change, so a box that had to be opened exists
      // by the time this runs. A layout effect would be too early — the element
      // is not mounted yet — and a timeout would be a race.
      queueMicrotask(() => {
        input.current?.focus();
        input.current?.select();
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [input, onBeforeFocus]);
}
